import React, { useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import "./PolicyStageFive.css";
import "./PaymentMethodModal.css";
import "../policy-stage-three/PolicyStageThree.css";
import cancelIcon from "../../../../images/cancel-icon.svg";
import boxEdgeVector from "../../../../images/box-edge-vector.svg";
import {
  IAllCargosInputStates,
  IDropdownOption,
  IPaystackReference,
  IStageFourPolicyFormStates,
  ISubmittedCertificateData,
  SetErrorHandlerType,
} from "../../../../Types";
import {
  ISingleUser,
  UserContext,
} from "../../../../components/contexts/UserContext";
import { ISavedIDArrayType } from "../../MarinePolicy";
import { registerACertificate } from "../../../../redux/actions/customerPoliciesActions";
import { useAppDispatch } from "../../../../hooks";
import LoadingSpinner from "../../../../components/loading-spinner/LoadingSpinner";
import {
  isPaystackReady,
  loadPaystackScript,
  openPaystackPayment,
} from "../../../../utils/paystack";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  allCargosInputStates: IAllCargosInputStates[];
  stageFourInputStates: IStageFourPolicyFormStates;
  savedCertificateId: ISavedIDArrayType;
  selectedCompanyFromBroker: ISingleUser | null;
  setStageFourInputStates: React.Dispatch<
    React.SetStateAction<IStageFourPolicyFormStates>
  >;
  policyType: string;
  setPageContent: React.Dispatch<React.SetStateAction<string>>;
  setSubmittedCertificatesData: React.Dispatch<
    React.SetStateAction<ISubmittedCertificateData[] | null>
  >;
  setErrorHandlerObj: SetErrorHandlerType;
  onRegistrationFailure: (message: string) => void;
}

function PaymentMethodModal({
  isOpen,
  onClose,
  allCargosInputStates,
  stageFourInputStates,
  savedCertificateId,
  selectedCompanyFromBroker,
  setStageFourInputStates,
  policyType,
  setPageContent,
  setSubmittedCertificatesData,
  setErrorHandlerObj,
  onRegistrationFailure,
}: IProps) {
  const { user }: any = useContext(UserContext);
  const dispatch = useAppDispatch();
  const [allPaymentMethodsArr, setAllPaymentMethodsArr] = useState<
    IDropdownOption[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaystackOpening, setIsPaystackOpening] = useState(false);
  const [isPaystackScriptReady, setIsPaystackScriptReady] = useState(false);
  const paystackCompletedRef = useRef(false);

  const getTotalPremiumPlanPrice = function () {
    const totalPremiumPlanPrice = allCargosInputStates?.reduce(
      (acc, obj) => ({
        premium: acc.premium + obj.premium,
      }),
      { premium: 0 }
    );
    return totalPremiumPlanPrice.premium;
  };

  const onPaystackSuccess = (reference: IPaystackReference) => {
    paystackCompletedRef.current = true;
    setIsPaystackOpening(false);
    const { reference: referenceValue } = reference;
    const data = {
      paymentMethod: "Paystack",
      creditNoteUrl: "",
      certificateIds: savedCertificateId ? savedCertificateId : [],
      reference: referenceValue,
      amount: Number(getTotalPremiumPlanPrice()),
    };
    setIsLoading(true);
    dispatch(
      registerACertificate(
        data,
        setErrorHandlerObj,
        setPageContent,
        setIsLoading,
        setSubmittedCertificatesData,
        onRegistrationFailure
      )
    );
  };

  const onPaystackClose = () => {
    if (paystackCompletedRef.current) {
      return;
    }

    setIsPaystackOpening(false);
    setIsLoading(false);
    setErrorHandlerObj({
      hasError: true,
      message: "Payment cancelled. Please try again or choose another method.",
    });
  };

  const handleProceedToPay = function () {
    const paystackAmount = Number(getTotalPremiumPlanPrice()) * 100;
    const publicKey = process.env.REACT_APP_PAYSTACK_KEY || "";

    if (!user?.email) {
      setErrorHandlerObj({
        hasError: true,
        message: "A valid email is required before you can pay with Paystack.",
      });
      return;
    }

    if (!publicKey) {
      setErrorHandlerObj({
        hasError: true,
        message: "Paystack is not configured. Please contact support.",
      });
      return;
    }

    if (!paystackAmount || paystackAmount <= 0) {
      setErrorHandlerObj({
        hasError: true,
        message: "Invalid payment amount. Please review your premium and try again.",
      });
      return;
    }

    if (!isPaystackReady()) {
      setErrorHandlerObj({
        hasError: true,
        message: "Paystack is still loading. Please wait a moment and try again.",
      });
      loadPaystackScript()
        .then(() => setIsPaystackScriptReady(true))
        .catch(() => undefined);
      return;
    }

    paystackCompletedRef.current = false;
    setErrorHandlerObj({ hasError: false, message: "" });

    flushSync(() => {
      setIsPaystackOpening(true);
    });

    try {
      openPaystackPayment(
        {
          publicKey,
          email: user.email,
          amount: paystackAmount,
          reference: new Date().getTime().toString(),
          currency: "NGN",
        },
        onPaystackSuccess,
        onPaystackClose
      );
    } catch {
      setIsPaystackOpening(false);
      setErrorHandlerObj({
        hasError: true,
        message: "Unable to open Paystack. Please try again.",
      });
    }
  };

  const registerCertificate = function (paymentMethodValue: string) {
    const data = {
      paymentMethod: paymentMethodValue,
      creditNoteUrl: "",
      certificateIds: savedCertificateId ? savedCertificateId : [],
    };

    setIsLoading(true);
    dispatch(
      registerACertificate(
        data,
        setErrorHandlerObj,
        setPageContent,
        setIsLoading,
        setSubmittedCertificatesData,
        onRegistrationFailure
      )
    );
  };

  const handlePaymentMethodSelect = function (paymentMethodValue: string) {
    if (isLoading || isPaystackOpening) {
      return;
    }

    setErrorHandlerObj({ hasError: false, message: "" });
    setStageFourInputStates({
      ...stageFourInputStates,
      paymentMethod: paymentMethodValue,
    });

    if (paymentMethodValue === "Paystack") {
      handleProceedToPay();
      return;
    }

    if (paymentMethodValue === "CreditNote") {
      onClose();
      setPageContent("stage-six");
      return;
    }

    const certificateCount = allCargosInputStates.length;

    if (user?.customerType === "Broker") {
      if (!selectedCompanyFromBroker) {
        return;
      }

      if (paymentMethodValue === "Unit") {
        if (selectedCompanyFromBroker.unit >= certificateCount) {
          registerCertificate(paymentMethodValue);
        } else {
          setErrorHandlerObj({
            hasError: true,
            message:
              "You don't have enough units to generate this certificate(s)!",
          });
        }
        return;
      }

      if (paymentMethodValue === "Wallet") {
        if (
          selectedCompanyFromBroker.walletBalance >= getTotalPremiumPlanPrice()
        ) {
          registerCertificate(paymentMethodValue);
        } else {
          setErrorHandlerObj({
            hasError: true,
            message:
              "You don't have enough premium balance to generate this certificate(s)!",
          });
        }
      }
      return;
    }

    if (paymentMethodValue === "Unit") {
      if (user.unit >= certificateCount) {
        registerCertificate(paymentMethodValue);
      } else {
        setErrorHandlerObj({
          hasError: true,
          message:
            "You don't have enough units to generate this certificate(s)!",
        });
      }
      return;
    }

    if (paymentMethodValue === "Wallet") {
      if (user?.walletBalance >= getTotalPremiumPlanPrice()) {
        registerCertificate(paymentMethodValue);
      } else {
        setErrorHandlerObj({
          hasError: true,
          message:
            "You don't have enough premium balance to generate this certificate(s)!",
        });
      }
    }
  };

  const handleReturnPaymentMethodArrForCustomers = function () {
    return [
      user?.paymentAccess?.isWalletEnabled && {
        value: "Use wallet",
        key: "Wallet",
      },
      user?.paymentAccess?.isUnitEnabled && {
        value: "Use units",
        key: "Unit",
      },
      user?.paymentAccess?.isRealTimePaymentEnabled && {
        value: "Pay now",
        key: "Paystack",
      },
    ];
  };

  const handleReturnPaymentMethodArrForBrokers = function () {
    return [
      selectedCompanyFromBroker?.paymentAccess?.isWalletEnabled && {
        value: "Use wallet",
        key: "Wallet",
      },
      selectedCompanyFromBroker?.paymentAccess?.isUnitEnabled && {
        value: "Use units",
        key: "Unit",
      },
      selectedCompanyFromBroker?.paymentAccess?.isRealTimePaymentEnabled && {
        value: "Pay now",
        key: "Paystack",
      },
    ];
  };

  useEffect(() => {
    if (isPaystackReady()) {
      setIsPaystackScriptReady(true);
      return;
    }

    loadPaystackScript()
      .then(() => setIsPaystackScriptReady(true))
      .catch(() => setIsPaystackScriptReady(false));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isPaystackReady()) {
      setIsPaystackScriptReady(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && policyType && user) {
      setIsLoading(false);
      setIsPaystackOpening(false);
      paystackCompletedRef.current = false;
      let paymentMethodsArr: Array<IDropdownOption | false | undefined> = [];
      if (policyType === "OpenCover") {
        if (user?.customerType === "Broker") {
          paymentMethodsArr = handleReturnPaymentMethodArrForBrokers();
        } else {
          paymentMethodsArr = handleReturnPaymentMethodArrForCustomers();
        }
      } else {
        paymentMethodsArr = [
          {
            value: "Credit Note",
            key: "CreditNote",
          },
          user?.paymentAccess?.isRealTimePaymentEnabled && {
            value: "Pay now",
            key: "Paystack",
          },
        ];
      }
      setAllPaymentMethodsArr(
        paymentMethodsArr.filter(Boolean) as IDropdownOption[]
      );
    }
  }, [isOpen, policyType, user, selectedCompanyFromBroker]);

  if (!isOpen || isPaystackOpening) {
    return null;
  }

  const isBusy = isLoading;

  return (
    <>
      <div
        className="payment-method-modal-overlay"
        onClick={() => {
          if (!isBusy) {
            onClose();
          }
        }}
      />
      <div
        className={`payment-method-modal-container ${
          isBusy ? "is-loading" : ""
        }`}
      >
        <div className="payment-method-modal-container--inner">
          <div className="cancel-modal-button-wrapper">
            <button type="button" onClick={onClose} disabled={isBusy}>
              <img src={cancelIcon} alt="" />
            </button>
          </div>

          <div className="policy-form-three-header-box-wrapper">
            <div className="box-edge-vector-wrapper">
              <img src={boxEdgeVector} alt="" />
            </div>
            <div className="form-header-box--sub-text">Total Premium Price</div>
            <div className="form-header-box--main-text">
              <span className="currency">
                {user?.currency === "USD" ? "$" : "₦"}
              </span>
              {user?.currency === "USD"
                ? (
                    Math.round(
                      (getTotalPremiumPlanPrice() / user?.exchangeRate) * 10
                    ) / 10
                  )?.toLocaleString() || ""
                : getTotalPremiumPlanPrice()?.toLocaleString() || ""}
            </div>
          </div>

          <div className="policy-plan-selection-container">
            <div className="policy-plan-selection-title-wrapper">
              Choose a payment method
            </div>

            <div
              className={`payment-method-selection-boxes-wrapper ${
                isBusy ? "is-loading" : ""
              }`}
            >
              {allPaymentMethodsArr?.map((paymentMethod, index) =>
                paymentMethod ? (
                  <div
                    key={index + 1}
                    className="payment-method-button-box-wrapper"
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePaymentMethodSelect(String(paymentMethod.key));
                      }}
                      className="payment-method-button-box-wrapper--inner"
                      disabled={
                        isBusy ||
                        (paymentMethod.key === "Paystack" &&
                          !isPaystackScriptReady)
                      }
                    >
                      {paymentMethod.key === "Paystack" &&
                      !isPaystackScriptReady
                        ? "Loading Paystack..."
                        : paymentMethod.value}
                    </button>
                  </div>
                ) : null
              )}
            </div>
          </div>

          {isBusy && (
            <div className="payment-method-modal-loading-overlay">
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PaymentMethodModal;
